import GameObject, { GameObjectEvent } from 'GameObject/GameObject';
import Renderable from 'Component/Renderable';
import { IPlayerStateQuestData } from 'State/Player/IPlayerState';
import QuestHelper from 'Utility/QuestHelper';
import { Retrier } from 'Utility/async';
import { report } from 'report';
import { attemptsToLoadBgTile } from 'Constants/Quests';
import CleanupManager from 'Utility/CleanupManager';
import { unexpectedValue } from 'Utility/Types';
import Game from 'Game';
import { MathHelper } from 'Utility/MathHelper';
import { IGameState } from 'State/Game/IGameState';
import { StartAnimation, StartAnimationClip, StopAnimation } from 'System/Animation/AnimationHelper';
import { AnimationClipEvent } from 'System/Animation/AnimationClip';
import { PlayerActions } from 'State/Player/PlayerActions';
import Armature from '../Armature';
import { QuestItemCompleteOutro } from 'System/Animation/Clip/QuestItemCompleteOutro';
import { AlphaPulseLoop } from 'System/Animation/Clip/AlphaPulseLoop';
import { Alpha } from 'System/Animation/Clip/Alpha';
import { ProgressBarSlideMotion } from 'System/Animation/Clip/ProgressBarSlideMotion';
import { isArray } from 'lodash';
import { QuestItemLayout } from 'Layouts/Quests/QuestItemLayout';
import TextComponent from 'Component/Render/Text';
import TransformComponent from 'Component/Transform';
import SpriteComponent from 'Component/Render/Sprite';
import { GLayout } from 'Utility/layout/LayoutHelper';
import { GameActions } from 'State/Game/GameActions';
import { ContextType } from 'State/Game/ContextType';
import { AdsConfig } from 'Constants/Ads';
import { AdvertisementArea } from 'Utility/SDK/Advertisement';
import { finishedSeasonPass } from 'State/Player/PlayerSeasonPassHelper';
import { SeasonPassPoints } from 'System/Animation/Clip';
import QuestsMenuHandler from 'System/UI/Menu/QuestsMenuHandler';
import { SEASON_PASS_TAB_PROGRESS_WIDTH, SeasonPassProgressUpdateAnimation } from 'System/UI/Menu/SeasonPassMenuHelper';
import { IRewardData } from 'Utility/RewardHelper';
import { getLastPositionMinusProgress } from 'System/UI/SeasonPass/TractorHelper';

export interface IQuestItemData {
    title: string;
    progress: number;
    icon: string;
    rewardIcon: string;
    rewardDesc: string;
}

interface IQuestItemParams {
    questIndex: number;
    layout: GLayout<QuestItemLayout>;
    data: IPlayerStateQuestData;
    questsMenuHandler: QuestsMenuHandler;
}

enum QuestStatus {
    LOADING = 'loading',
    LOADED = 'loaded',
}

export const progressBarFillMaxWidth: number = 241;

export default class QuestItem {
    private layout: GLayout<QuestItemLayout>;
    private animating: boolean = false;
    private questIndex: number;
    private collectDelay: number = 0;
    private tileLoaded: boolean = false;
    private status: QuestStatus;
    private questData: IPlayerStateQuestData;
    private questsMenuHandler: QuestsMenuHandler;
    private containerCleanupManager: CleanupManager = new CleanupManager();
    private readonly loadTileRetrier: Retrier = new Retrier(undefined, (error, attemptIndex) => {
        if (attemptIndex === attemptsToLoadBgTile) {
            report(`Failed to load quest background tile ${attemptsToLoadBgTile} times in row`, {
                error,
                once: true,
                sampleRate: 0.1,
                fingerprint: 'QuestAssetLoadingError',
                tags: {
                    questType: this.questData?.type,
                    tier: this.questData?.tier,
                },
            });
        }
    });

    constructor(params: IQuestItemParams) {
        this.layout = params.layout;
        this.questData = params.data;
        this.questIndex = params.questIndex;
        this.questsMenuHandler = params.questsMenuHandler;
    }

    public get isAnimating(): boolean {
        return this.animating;
    }

    public updateData(data: IPlayerStateQuestData, _state: IGameState = Game.store.getState()) {
        if (!data || this.animating || this.layout.root.destroyed) {
            return;
        }

        const isNewQuest = this.questData?.id !== data.id;

        const progressChanged = data.progress !== this.questData?.progress;
        this.questData = { ...data };
        const status = isNewQuest ? QuestStatus.LOADING : this.getStatus(this.questData);
        if (status !== this.status || isNewQuest) {
            // Handle change of status
            switch (status) {
                case QuestStatus.LOADING:
                    // Display loading container
                    const loadingContainer = this.layout.loadingContainer;
                    this.initializeContainer(loadingContainer);

                    // Begin to attempt to load background image
                    this.loadTileRetrier.restart(() => this.loadTile(), false).then(this.onTileLoaded);
                    break;
                case QuestStatus.LOADED:
                    break;
                default:
                    unexpectedValue(status);
            }
            // Update status
            this.status = status;
        } else if (status === QuestStatus.LOADED) {
            // Update progress
            if (progressChanged) {
                this.updateActiveQuest(this.questData);
            }
        }
    }

    public destroy() {
        this.containerCleanupManager.cleanUpAll();
    }

    public onCollectClicked = (delay = 0) => {
        if (this.animating) return;
        const container = this.layout.eventBG;
        StopAnimation(container);
        const activeContainer = this.layout.activeContainer;
        const completeContainer = this.layout.completedContainer;
        const completeText = this.layout.completeText;
        this.animating = true;
        this.collectDelay = 0;
        const state = Game.store.getState();

        // Create check animation
        const checkDBContainer = this.layout.completedDBContainer;
        const check = GameObject.Instantiate(Armature, {
            dragonbonesName: 'quest_complete_animations',
            armatureName: 'check',
            alpha: 0,
            playOnStart: false,
        });
        checkDBContainer.addChild(check);

        PlayerActions.claimQuestReward(this.questIndex);

        StartAnimationClip(
            new QuestItemCompleteOutro(container, {
                initialDelay: 750 + delay,
                activeContainer,
                completeContainer,
                completeText,
                check,
            }).once(AnimationClipEvent.DESTROY, () => {
                this.animating = false;
                const state = Game.store.getState();
                this.updateData(state.playerState.quests[this.questIndex], state);
                this.questsMenuHandler.checkCollectableQuests();
            })
        );
    };
    public handleStateChanged = (state: IGameState) => {
        this.updateData(state.playerState.quests[this.questIndex], state);
    };
    private readonly onTileLoaded = () => {
        if (this.layout.root.destroyed) return;

        this.tileLoaded = true;
        this.containerCleanupManager.cleanUpAll();
        this.containerCleanupManager.add(() => {
            this.tileLoaded = false;
        });
        const questContainer = this.layout.eventBG;
        this.setupQuest(this.questData);
        this.initializeContainer(questContainer, false);
        this.updateActiveQuest(this.questData);
    };
    private updateActiveQuest(data: IPlayerStateQuestData) {
        this.setProgress();

        // Set bg visibility
        this.backgroundActive.getComponent(Renderable).visible = true;
        this.backgroundCompleted.getComponent(Renderable).visible = false;

        const canComplete = QuestHelper.canCompleteQuest(data);
        const container = this.layout.eventBG;
        if (canComplete) {
            const adButton = this.layout.adButton;
            const borderGlow = this.layout.bgGlow;

            // DB reward shine animaiton
            const rewardDBContainer = this.layout.rewardDBContainer;
            const rewardShine = GameObject.Instantiate(Armature, {
                dragonbonesName: 'quest_complete_animations',
                armatureName: 'reward_rays',
                alpha: 0,
            });
            rewardDBContainer.addChild(rewardShine);
            this.containerCleanupManager.add(() => {
                rewardShine.destroy();
            });

            // Season pass reward shine animaiton
            let rewardSPShine: Armature = null;
            StartAnimation(container, 'QuestItemCompleteIntro', {
                initialDelay: 750,
                borderGlow,
                rewardShine,
                rewardSPShine,
                backgroundCompleted: this.backgroundCompleted,
                backgroundActive: this.backgroundActive,
                adButton
            }).on(AnimationClipEvent.COMPLETE, () => {
                this.onCollectClicked(this.collectDelay);
                const progressShiny = this.layout.progressBarFillShiny;
                StartAnimationClip(
                    new ProgressBarSlideMotion(progressShiny, {
                        duration: 500,
                        delay: 1000,
                        progressBarFillMaxWidth,
                    })
                );
                this.containerCleanupManager.add(() => {
                    StopAnimation(progressShiny);
                    progressShiny?.updateComponent(Renderable, { alpha: 0 });
                });
            });
        } else {
            StartAnimationClip(
                new AlphaPulseLoop(this.layout.bgShiny, {
                    duration: 500,
                    alpha: [0, 1],
                    delay: 1000,
                })
            );
        }
    }
    private loadTile(): Promise<void> {
        QuestHelper.addTileIfNeeded(this.questData.type, this.questData.tier);
        return Game.resource.load();
    }
    private getStatus(data: IPlayerStateQuestData) {
        if (this.tileLoaded) {
            return QuestStatus.LOADED;
        }
        return QuestStatus.LOADING;
    }
    private setupQuest(data: IPlayerStateQuestData) {
        const canComplete = QuestHelper.canCompleteQuest(data);
        const target = QuestHelper.getFinalQuestTarget(
            data.type,
            data.tier,
            QuestHelper.getQuestPoolKeyByEventType(data.eventType)
        );
        const rewards = QuestHelper.getQuestRewards(data.tier);
        const reward = rewards[0];
        if (isArray(reward)) {
            report('Multi reward quests are not implemented for standard quests', {
                once: true,
                sampleRate: 0.01,
                tags: {
                    tier: data.tier,
                    type: data.type,
                },
                fingerprint: 'setupQuest:MultiRewardNotImplemented',
            });
            return;
        }

        // Set quest background
        const container = this.layout.eventBG;
        const questBg = QuestHelper.getBackgroundTileKey(data.type, data.tier);
        container.getComponent(SpriteComponent).texture = questBg;

        // Show quest details
        const activeContainer = this.layout.activeContainer;

        activeContainer.updateComponent(Renderable, { visible: true, alpha: 1 });
        this.backgroundActive.updateComponent(Renderable, { visible: true, alpha: 1 });

        // Set quest title
        const questTitle = this.layout.questTitle;
        const questTitleText = Game.locale.getText(QuestHelper.getTitleKey(data.type));
        questTitle.getComponent(TextComponent).text = questTitleText;

        // Set progress text
        const progressBarText = this.layout.progressText;
        const progressText = `${data.progress} / ${target}`;
        progressBarText.getComponent(TextComponent).text = progressText;

        // Set reward sprite
        const rewardSprite = this.layout.rewardSprite;
        rewardSprite.getComponent(SpriteComponent).texture = reward.iconCacheKey;
        rewardSprite.getComponent(TransformComponent).scale.set(reward.iconScale[0]);
        rewardSprite.getComponent(TransformComponent).angle = reward.angle;

        // Set reward text amount
        const rewardText = this.layout.rewardText;
        rewardText.getComponent(TextComponent).text = String(reward.value);

        // hide rewardSPContainer if season pass is completed
        if (finishedSeasonPass(Game.store.getState().playerState)) {
            this.layout.rewardMainContainer.getComponent(TransformComponent).x = 0;
        }

        const adButton = this.layout.adButton;
        adButton.on(GameObjectEvent.SELECTED, this.onShowAdClicked);
        this.containerCleanupManager.add(() => {
            adButton.off(GameObjectEvent.SELECTED, this.onShowAdClicked);
        });
        adButton.getComponent(Renderable).visible = !canComplete;
        adButton.getComponent(Renderable).alpha = canComplete ? 0 : 1;

        // Set background glow visibility
        const backgroundGlow = this.layout.bgGlow;
        backgroundGlow.getComponent(Renderable).visible = !canComplete;

        // Set background shiny visibility
        const backgroundShiny = this.layout.bgShiny;
        backgroundShiny.getComponent(Renderable).visible = canComplete;

        // Set Progress bar fill sprite
        const progressBarFill = this.layout.progressBarFill;
        progressBarFill.getComponent(SpriteComponent).texture = canComplete ? 'mission_bar_green' : 'mission_bar_blue';
    }

    private initializeContainer(container: GameObject, hide = true) {
        this.containerCleanupManager.add(() => {
            if (hide)
                StartAnimationClip(
                    new Alpha(container, {
                        startAlpha: 1,
                        endAlpha: 0,
                        duration: 450,
                    })
                );
        });
        StartAnimationClip(
            new Alpha(
                container,
                {
                    startAlpha: 0,
                    endAlpha: 1,
                    duration: 200,
                },
                true
            )
        );
    }
    private setProgress() {
        const fill = this.progressBarFill;
        const text = this.progressBarText;
        const target = QuestHelper.getQuestTarget(this.questData.type, this.questData.tier);

        const fillRenderable = fill.getComponent(Renderable);

        if (isArray(target)) {
            // TODO: Implement multistage quests into normal quests
            // (not sure if this class will stay for long)
            console.warn('Multi-stage quests not implemented for non-event quests');
            return;
        }

        // Set bar fill length
        if (this.questData.progress !== 0) {
            fillRenderable.visible = true;
            const percProgress = MathHelper.normalize(this.questData.progress, 0, target);
            fill.getComponent(TransformComponent).width = Math.min(
                MathHelper.lerp(0, progressBarFillMaxWidth, percProgress),
                progressBarFillMaxWidth
            );
        } else {
            fillRenderable.visible = false;
        }
        let currentProgress = MathHelper.clamp(this.questData.progress, 0, target);
        // Set Progress text
        text.getComponent(TextComponent).text = `${currentProgress} / ${target}`;
    }

    private readonly onShowAdClicked = () => {
        if (this.questsMenuHandler.blockClose) return;
        GameActions.setContext(ContextType.DISPLAY_ADVERTISEMENT, {
            placement: AdsConfig.getNewQuest(Game.config),
            payload: {
                g_gameArea: AdvertisementArea.CHANGE_MISSION,
                questIndex: this.questIndex,
            },
        });
    };

    private get progressBarFill() {
        return this.layout.progressBarFill;
    }
    private get progressBarText() {
        return this.layout.progressText;
    }
    private get backgroundActive() {
        return this.layout.bgActive;
    }
    private get backgroundCompleted() {
        return this.layout.bgCompleted;
    }
    public get activeContainer() {
        return this.layout.activeContainer;
    }
    public get completedContainer() {
        return this.layout.completedContainer;
    }
    public setCollectDelay(delay: number) {
        this.collectDelay = delay;
    }
}
